import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, User, ArrowRight, Search } from 'lucide-react';
import { blogPosts } from '../data/mock';
import './BlogPage.css';

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  const categories = ['all', ...new Set(blogPosts.map(post => post.category))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', { 
      day: 'numeric',
      month: 'long', 
      year: 'numeric' 
    }).format(date);
  };

  return (
    <div className="blog-page">
      <section className="blog-hero">
        <div className="section-container">
          <div className="blog-hero-content animate-on-scroll">
            <h1 className="blog-title">
              Ressources & <span className="text-gradient">Expertise</span>
            </h1>
            <p className="blog-subtitle">
              Guides pratiques, conseils et actualités pour structurer et développer votre entreprise
            </p>
          </div>
        </div>
      </section>

      <section className="blog-filters">
        <div className="section-container">
          <div className="filters-wrapper animate-on-scroll">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="category-filters">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'Tous' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {filteredPosts.length > 0 && (
        <section className="featured-post-section section-padding">
          <div className="section-container">
            <div className="featured-post animate-on-scroll">
              <div className="featured-image">
                <img src={filteredPosts[0].image} alt={filteredPosts[0].title} />
                <div className="featured-category">{filteredPosts[0].category}</div>
              </div>
              <div className="featured-content">
                <h2>{filteredPosts[0].title}</h2>
                <p>{filteredPosts[0].excerpt}</p>
                <div className="featured-meta">
                  <div className="meta-item">
                    <User size={16} />
                    <span>{filteredPosts[0].author}</span>
                  </div>
                  <div className="meta-item">
                    <Clock size={16} />
                    <span>{filteredPosts[0].readTime} de lecture</span>
                  </div>
                  <div className="meta-item">
                    <span>{formatDate(filteredPosts[0].date)}</span>
                  </div>
                </div>
                <button className="btn btn-primary">
                  Lire l'article <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="section-padding">
        <div className="section-container">
          {filteredPosts.length > 0 ? (
            <div className="blog-grid">
              {filteredPosts.slice(1).map((post) => (
                <article key={post.id} className="blog-card animate-on-scroll">
                  <div className="blog-card-image">
                    <img src={post.image} alt={post.title} />
                    <div className="blog-card-category">{post.category}</div>
                  </div>
                  <div className="blog-card-content">
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="blog-card-meta">
                      <div className="meta-item">
                        <User size={14} />
                        <span>{post.author}</span>
                      </div>
                      <div className="meta-item">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <button className="blog-card-link">
                      Lire la suite <ArrowRight size={16} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="no-results animate-on-scroll">
              <p>Aucun article trouvé pour votre recherche.</p>
            </div>
          )}
        </div>
      </section>

      <section className="newsletter-section">
        <div className="section-container">
          <div className="newsletter-card animate-on-scroll">
            <h2>Restez informé</h2>
            <p>Recevez nos derniers articles et conseils directement dans votre boîte mail</p>
            <form className="newsletter-form">
              <input
                type="email"
                placeholder="Votre adresse email"
                required
              />
              <button type="submit" className="btn btn-primary">
                S'abonner
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="blog-cta">
        <div className="section-container">
          <div className="cta-content animate-on-scroll">
            <h2 className="cta-title">Besoin d'un accompagnement personnalisé ?</h2>
            <p className="cta-text">
              Discutons de vos enjeux de structuration lors d'un diagnostic gratuit
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Prendre rendez-vous <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;